import { useMutation } from "@apollo/client";
import {
  DuplicateEventDocument,
  EventFragment,
  ProjectDetailsDocument,
} from "../../../../__generated__/graphql";
import Button from "../../../Button";
import { IoDuplicate } from "react-icons/io5";
import Modal from "../../../Modal";
import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FormInput from "../../../FormInput";
import Checkbox from "../../../Checkbox";
import toast from "react-hot-toast";
import ProjectsSelection from "../../../ProjectsSelection";
import LocationSearchPicker from "../../../LocationSearchPicker";

interface EventDuplicateButtonProps {
  event: EventFragment;
  onNewEvent: (event: EventFragment) => void;
}

const duplicationEventSchema = z.object({
  eventName: z.string().min(1, "Un nom est requis"),
  projectId: z.string().optional(),
  startDate: z
    .string()
    .min(1, "La date est obligatoire")
    .transform((val) => new Date(val).toISOString()),
  locationLabel: z.string().optional(),
  locationAddress: z.string().optional(),
  locationZipCode: z.string().optional(),
  locationCity: z.string().optional(),
  locationLatitude: z.number().optional(),
  locationLongitude: z.number().optional(),
  locationPhotoUrls: z.array(z.string()).optional(),
  copyActivities: z.boolean(),
});

type DuplicationEventFormData = z.infer<typeof duplicationEventSchema>;

export default function EventDuplicateButton({
  event,
  onNewEvent,
}: EventDuplicateButtonProps) {
  const [searchOnGoogleMaps, setSearchOnGoogleMaps] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateEventMutation, { loading: isDuplicatingEvent }] = useMutation(
    DuplicateEventDocument,
    {
      onCompleted: (data) => {
        toast.success("Évènement dupliqué avec succès");
        setIsDuplicateDialogOpen(false);
        onNewEvent(data.duplicateEvent);
      },
      onError: () => {
        toast.error(
          "Une erreur est survenue lors de la duplication de l'évènement"
        );
        setIsDuplicateDialogOpen(false);
      },
    }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<DuplicationEventFormData>({
    resolver: zodResolver(duplicationEventSchema),
    defaultValues: {
      projectId: event.projectId ?? undefined,
      locationLabel: event.eventLocation?.location?.label ?? undefined,
      locationAddress: event.eventLocation?.location?.address ?? undefined,
      locationZipCode: event.eventLocation?.location?.zipCode ?? undefined,
      locationCity: event.eventLocation?.location?.city ?? undefined,
      locationLatitude: event.eventLocation?.location?.latitude ?? undefined,
      locationLongitude: event.eventLocation?.location?.longitude ?? undefined,
      locationPhotoUrls: event.eventLocation?.location?.photoUrls ?? undefined,
      copyActivities: true,
    },
  });

  const onSubmit = async (data: DuplicationEventFormData) => {
    await duplicateEventMutation({
      variables: {
        duplicateEventInput: {
          eventId: event.id,
          options: {
            newProjectId: data.projectId,
            newName: data.eventName,
            newStartDate: data.startDate,
            newLocationLabel: data.locationLabel,
            newLocationAddress: data.locationAddress,
            newLocationZipCode: data.locationZipCode,
            newLocationCity: data.locationCity,
            newLocationLatitude: data.locationLatitude,
            newLocationLongitude: data.locationLongitude,
            newLocationPhotoUrls: data.locationPhotoUrls,
            copyActivities: data.copyActivities,
          },
        },
      },
      refetchQueries: [
        ...(data.projectId
          ? [
              {
                query: ProjectDetailsDocument,
                variables: {
                  projectId: data.projectId,
                },
              },
            ]
          : []),
      ],
    });
  };

  const locationInfos = () => {
    if (searchOnGoogleMaps) {
      return (
        <div className="flex flex-col gap-2 min-w-md">
          <div className="text-md font-medium">Lieu</div>
          <LocationSearchPicker
            onLocationSelect={(location) => {
              setSearchOnGoogleMaps(false);
              setValue("locationLabel", location.name);
              setValue("locationAddress", location.address);
              setValue("locationZipCode", location.zipcode || "");
              setValue("locationCity", location.city || "");
              setValue("locationLatitude", location.latitude);
              setValue("locationLongitude", location.longitude);
              setValue("locationPhotoUrls", location.photoUrls || []);
            }}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <div className="text-md font-medium">Lieu</div>
          <button
            className="text-accent hover:text-accent/80 transition-all duration-300 cursor-pointer"
            onClick={() => setSearchOnGoogleMaps(true)}
          >
            <div className="flex items-center gap-2 text-sm">
              Rechercher avec Google Maps
            </div>
          </button>
        </div>
        <div>
          <div className="text-sm font-medium text-secondary">Nom du lieu</div>
          <div className="text-sm text-primary">{watch("locationLabel")}</div>
          <div className="text-sm font-medium text-secondary">Adresse</div>
          <div className="text-sm text-primary">{watch("locationAddress")}</div>
          <div className="text-sm font-medium text-secondary">Code postal</div>
          <div className="text-sm text-primary">{watch("locationZipCode")}</div>
          <div className="text-sm font-medium text-secondary">Ville</div>
          <div className="text-sm text-primary">{watch("locationCity")}</div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        confirmClose
        isModalOpen={isDuplicateDialogOpen}
        onClose={() => {
          setIsDuplicateDialogOpen(false);
          reset();
        }}
        title="Dupliquer l'évènement"
        description="Renseignez les informations du nouvel évènement."
        className="max-w-[700px] min-w-[500px]"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <FormInput
            label="Nom du nouvel évènement"
            error={errors.eventName}
            id="eventName"
            register={register}
          />
          <FormInput
            label="Date du nouvel évènement"
            error={errors.startDate}
            id="startDate"
            type="date"
            register={register}
          />
          <label
            htmlFor="projectId"
            className="block text-sm font-medium -mb-2"
          >
            Choisir un projet
            <span className="text-sm text-tertiary"> (optionnel)</span>
          </label>
          <ProjectsSelection
            selectedProjectId={watch("projectId")}
            onProjectSelect={(project) => setValue("projectId", project?.id)}
            placeholder="Sélectionner un projet"
            error={errors.projectId?.message}
          />

          {locationInfos()}

          <div className="flex items-center gap-2">
            <Checkbox
              checked={watch("copyActivities")}
              onChange={(checked) => setValue("copyActivities", checked)}
            />
            <div className="text-sm">Dupliquer les activités</div>
          </div>

          <div className="flex justify-between items-center">
            <Button
              variant="secondary"
              onClick={() => setIsDuplicateDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              isLoading={isDuplicatingEvent}
              type="submit"
            >
              Dupliquer
            </Button>
          </div>
        </form>
      </Modal>

      <Button
        variant="secondary"
        className="flex flex-row gap-2 text-sm text-secondary border-0 hover:text-secondary/80"
        onClick={() => setIsDuplicateDialogOpen(true)}
      >
        <IoDuplicate className="w-4 h-4" />
        Dupliquer
      </Button>
    </>
  );
}
